const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/modules/CIS4m/components/CIS4mAutocomplete.tsx';
import * as React from 'react';
import { forwardRef, ReactElement, useEffect, useState } from 'react';
import { useCIS4mValidation } from '../hooks';
import { CIS4mValueComponentProps } from '../types';
import { Autocomplete as MUIAutocomplete, TextField, TextFieldProps } from '@mui/material';
import textMap from './translations';
import { KeyValuePair } from '../../../defs/generalDefs';
import { useCIS4mContext } from '../context';
import { TextInput, useText } from '../../../utils/intlUtils';

interface Props extends Omit<CIS4mValueComponentProps, 'initialValue'> {
    label: TextInput;
    required?: boolean;
    options: any[];
    freeSolo?: boolean;
    className?: string;
    renderOption?: (props: KeyValuePair, option: any) => ReactElement<any>;
    getOptionLabel?: (option: any) => string;
    getOptionKey?: (option: any) => string | number;
    getOptionValue?: (option: any) => any;
    onChange?: (value: any) => void;
    maxLength?: number;
    noOptionsText?: TextInput;
}

const sxTextField: TextFieldProps['sx'] = {
    mt: 0.5,
    mb: 0.5,
};

export const CIS4mAutocomplete = forwardRef((props: Props, ref: any) => {
    const {
        className,
        getOptionKey,
        getOptionLabel,
        getOptionValue,
        maxLength,
        onChange,
        options,
        path,
        renderOption,
    } = props;
    const { variant } = useCIS4mContext().style;

    const hidden = props.hidden ?? false;
    const required = props.required ?? false;
    const readonly = props.readonly ?? false;
    const disabled = props.disabled ?? false;
    const freeSolo = props.freeSolo ?? true;

    const label = useText(props.label);
    const noOptionsText = useText(props.noOptionsText ?? textMap.noSearchResults);

    const [storedCIS4mValue, setStoredCIS4mValue, errorMessage] = useCIS4mValidation(ref, {
        disabled,
        path,
        initialValue: undefined,
        hidden,
        required,
        errorMessageWhenRequired: freeSolo ? undefined : textMap.searchOptionIsRequired,
    });

    const [stateValue, setStateValue] = useState<string | KeyValuePair>(storedCIS4mValue);

    useEffect(() => {
        // if the stored value changes dynamically, we need to update the state value
        if (storedCIS4mValue !== stateValue) {
            setStateValue(storedCIS4mValue);
        }
    }, [storedCIS4mValue]);

    if (hidden) return null;

    const value = stateValue || '';

    // Fired by MUI's Autocomplete for input-text changes. We only react to actual user
    // typing (reason === 'input'); option selection is handled by handleAutocompleteChange,
    // and MUI resets (reason 'reset'/'clear') must not overwrite the stored value.
    const handleInputChange = (_event: any, newInputValue: string, reason: string) => {
        if (reason !== 'input') {
            return;
        }
        // The value stored in the form data can be the value of the input,
        // but only when it's "freeSolo" (a non option value is accepted)
        if (freeSolo) {
            setStoredCIS4mValue(newInputValue);
        }
        setStateValue(newInputValue);
        if (onChange) onChange(newInputValue);
    };

    const handleAutocompleteChange = (event: any, newValue: any) => {
        const newCIS4mValue = getOptionValue ? getOptionValue(newValue) : newValue;

        setStoredCIS4mValue(newCIS4mValue);
        setStateValue(newValue);
        if (onChange) onChange(newValue);
    };

    const autocompleteRenderInput = (params: any) => (
        <TextField
            {...params}
            label={label}
            variant={variant}
            size="small"
            slotProps={{
                ...params.slotProps,
                htmlInput: {
                    ...params.slotProps?.htmlInput,
                    type: 'search',
                    readOnly: readonly,
                    maxLength: maxLength,
                },
            }}
            color={!!errorMessage ? 'error' : 'primary'}
            required={required}
            disabled={disabled}
            hidden={hidden}
            error={!!errorMessage}
            helperText={errorMessage}
            sx={sxTextField}
        />
    );

    // Additional props that should only be added when actually defined
    const additionalAutocompleteProps = {
        renderOption,
        getOptionLabel,
    };

    return (
        <MUIAutocomplete
            className={className}
            freeSolo={freeSolo}
            disableClearable
            value={value}
            options={options}
            renderInput={autocompleteRenderInput}
            onChange={handleAutocompleteChange}
            onInputChange={handleInputChange}
            getOptionKey={getOptionKey}
            readOnly={readonly}
            noOptionsText={noOptionsText}
            {...additionalAutocompleteProps}
        />
    );
});

CIS4mAutocomplete.displayName = 'CIS4mAutocomplete';
